import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useMutation } from '@apollo/client/react';
import { ACK_DUNNING_NOTICE } from '@scio3/graphql-client';
import type { AckDunningNoticeResult, AckDunningNoticeVariables } from '@scio3/graphql-client';
import {
  ADVISORY_DISMISS,
  DUNNING_LADDERS,
  type CompanyDunningAdvisory,
} from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { advisoryKey, useDunningDismissStore } from '@/stores/dunning-dismiss-store';
import { advisoryPayPath } from '@/lib/dunning-pay-path';
import { useCanOpenPayPath } from '@/hooks/useCanOpenPayPath';
import { readDunning } from './useDunningState';

/**
 * The pre-emptive advisory banner — CC day −30 (card expiring) and day −7
 * (upcoming charge), PO day −7 (upcoming invoice).
 *
 * This is NOT dunning. Nothing is past due, nothing is limited, no screen is at
 * risk yet. It is a dismissible heads-up, and the distinction is why it gets a
 * banner with an X and never a modal — interrupting someone over a card that
 * expires in 30 days would be out of all proportion.
 *
 * **The dismissal is a tri-state**, mirroring `payment.trialBanner` (0 / 1 / 2):
 * one dismissal buys quiet until the date is genuinely close, then it returns once,
 * and a second dismissal is final. "Your card expires in 30 days" is trivially easy
 * to wave away and still perfectly true at 29 — a single permanent dismissal would
 * quietly guarantee the failure this rung exists to prevent.
 *
 * The countdown is evaluated client-side against `advisoryAt`, the same way
 * `usePromotionalBanner` derives `daysLeft` from `trialEndDate` — the visibility
 * rule is time-dependent, so a server-computed boolean would go stale between
 * `me` calls.
 *
 * Source of truth is `me.payment.dunningAdvisory`, which the backend re-reads fresh
 * from Mongo on every `me` call.
 */

/** Pages where a banner would break a full-bleed editing surface. */
const EXCLUDED_PATHS = ['/app/designManagement'];

/** Steps that may legitimately appear as an advisory. */
const ADVISORY_STEPS: Record<string, true> = {
  annualRenewalCharge: true,
  annualRenewalInvoice: true,
  cardExpiry: true,
  upcomingCharge: true,
  upcomingInvoice: true,
};

/**
 * Narrow the untyped `me.payment` blob. A type guard rather than a cast: a
 * malformed value must render nothing rather than crash the shell.
 */
function readAdvisory(payment: Record<string, unknown> | undefined): CompanyDunningAdvisory | null {
  const raw = payment?.dunningAdvisory;
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Partial<CompanyDunningAdvisory>;
  if (typeof candidate.step !== 'string' || typeof candidate.ladder !== 'string') return null;
  // Only the real pre-emptive rungs. Guards against anything else being
  // surfaced here as a friendly dismissible notice.
  if (!ADVISORY_STEPS[candidate.step]) return null;
  return candidate as CompanyDunningAdvisory;
}

/** Whole days until the warned-about date, or null if there isn't a usable one. */
export function daysUntil(value: string | null | undefined, now: Date): number | null {
  if (!value) return null;
  const t = new Date(value);
  if (Number.isNaN(t.getTime())) return null;
  return Math.ceil((t.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * The tri-state visibility rule. Mirrors `shouldShowAdvisory` in the backend
 * engine — kept in sync deliberately, and unit-tested on both sides, because the
 * countdown has to be evaluated at render time.
 */
export function shouldShowAdvisory(advisory: CompanyDunningAdvisory | null, now: Date): boolean {
  if (!advisory) return false;

  const dismiss = advisory.dismissState ?? ADVISORY_DISMISS.SHOW;
  if (dismiss >= ADVISORY_DISMISS.PERMANENT) return false;

  const days = daysUntil(advisory.advisoryAt, now);
  // No usable date: show while undismissed rather than hide a real warning, but a
  // single dismissal is then final since there is no countdown to return on.
  if (days === null) return dismiss === ADVISORY_DISMISS.SHOW;

  // The date has arrived or passed — either the charge succeeded, or it failed and
  // a real dunning episode owns the message now.
  if (days < 0) return false;

  if (dismiss === ADVISORY_DISMISS.SHOW) return true;

  const window = advisory.reshowWithinDays ?? 0;
  return window > 0 && days <= window;
}

export interface DunningAdvisoryResult {
  advisory: CompanyDunningAdvisory | null;
  /** Show the dismissible advisory banner. */
  showAdvisory: boolean;
  /** Which rung — drives the copy. */
  step: string | null;
  /** True for a PO / net-terms account: changes copy and CTA. */
  isInvoiceBilled: boolean;
  /** Where the pay CTA goes. Same rule as the episode surfaces. */
  payPath: string;
  /** The viewer can open `payPath`; false hides the CTA (see `canOpenPayPath`). */
  canPay: boolean;
  /** Days until the warned-about date, for the copy. */
  daysRemaining: number | null;
  /** Record the click. `dismissed` also advances the tri-state server-side. */
  acknowledge: (action: 'paid' | 'dismissed') => void;
}

export function useDunningAdvisory(): DunningAdvisoryResult {
  const location = useLocation();
  const me = useAuthStore((s) => s.me);

  const dismissed = useDunningDismissStore((s) => s.dismissed);
  const dismiss = useDunningDismissStore((s) => s.dismiss);

  const [ackNotice] = useMutation<AckDunningNoticeResult, AckDunningNoticeVariables>(
    ACK_DUNNING_NOTICE,
  );

  const advisory = useMemo(() => readAdvisory(me?.payment), [me?.payment]);

  // Evaluated at render time — the visibility rule depends on "how far away is the
  // date", which changes without any new data arriving.
  const now = new Date();

  const isExcludedPath = EXCLUDED_PATHS.some((path) => location.pathname.startsWith(path));

  // A real past-due episode outranks the advisory: the customer is already being
  // told something stronger, and two billing banners is the "looks like marketing"
  // failure that makes both invisible.
  //
  // Asks `readDunning`, the SAME predicate the episode surfaces use. This used to read
  // `payment.dunning.state` — a field the backend stopped sending when the projection
  // shrank to four fields — so `hasEpisode` was permanently false and the advisory was
  // never suppressed at all. Nothing threw; the guard just quietly stopped guarding.
  //
  // An episode that renders nothing is still "no episode" here, which preserves the old
  // `state: 'none'` behaviour: suppressing on it hid the advisory while the episode
  // banner was also off, and the customer saw neither.
  const hasEpisode = readDunning(me?.payment as Record<string, unknown> | undefined) !== null;

  // Shared across every caller of this hook (see dunning-dismiss-store), so
  // AppShell's precedence decision sees a dismissal the banner made.
  const key = advisory
    ? advisoryKey(me?.accountId, advisory.step, advisory.advisoryAt as string | null)
    : null;
  const locallyDismissed = !!key && dismissed.has(key);

  const showAdvisory =
    !!me &&
    !hasEpisode &&
    !locallyDismissed &&
    !isExcludedPath &&
    shouldShowAdvisory(advisory, now);

  const acknowledge = useCallback(
    (action: 'paid' | 'dismissed') => {
      if (!advisory) return;

      // Dismissing hides it immediately; clicking through to pay leaves it up,
      // because they haven't actually fixed anything yet.
      if (action === 'dismissed' && key) dismiss(key);

      // Fire-and-forget: failing to record a dismissal must never trap the customer
      // behind an error on a purely informational banner.
      ackNotice({
        variables: { payload: { step: advisory.step, action } },
      }).catch(() => {
        // Intentionally swallowed — the local dismissal already applied.
      });
    },
    [ackNotice, advisory, dismiss, key],
  );

  const payPath = advisoryPayPath(advisory?.ladder === DUNNING_LADDERS.PO);
  const canPay = useCanOpenPayPath(payPath);

  return {
    advisory,
    showAdvisory,
    step: advisory?.step ?? null,
    isInvoiceBilled: advisory?.ladder === DUNNING_LADDERS.PO,
    payPath,
    canPay,
    daysRemaining: daysUntil(advisory?.advisoryAt, now),
    acknowledge,
  };
}
